<template>
  <Flex
    direction="column"
    :gap=".5"
  >
    <BaseTask
      v-for="(task, index) in tasks"
      :key="index"
      :text="task.text"
      :state="state(task)"
      @click="onTaskClick(task.name)"
      v-test="'wavy-onboarding-task'"
    />
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useCompanyStore } from '@/stores/company';
import type { OnboardingTask, WavyMigrationStatus } from '@/types';

const { activeSubscription } = storeToRefs(useCompanyStore());

const { t } = useI18n();

const props = defineProps<{
  taskData: OnboardingTask[];
  migrationStatus?: WavyMigrationStatus;
}>();

const state = (task) => {
  if (
    task.name === 'trigger_wavy_migration' &&
    props.migrationStatus === 'IN_PROGRESS'
  ) {
    return 'pending';
  }

  if (task.completedAt) {
    return 'completed';
  }

  if (task.name === 'migrate_website') {
    const wavyMigrationTask = tasks.value.find(
      (task) => task.name === 'trigger_wavy_migration',
    );
    if (!wavyMigrationTask?.completedAt) {
      return 'blocked';
    }
  }

  if (
    !activeSubscription.value &&
    (task.name === 'stop_wavy_subscription' || task.name === 'migrate_website')
  ) {
    return 'blocked';
  }

  return 'default';
};

const taskTexts = computed(() => [
  {
    name: 'setup_company_details',
    text: t('wavy_onboarding.trial_tasks.tasks.company_information'),
  },
  {
    name: 'trigger_wavy_migration',
    text: t('wavy_onboarding.trial_tasks.tasks.copy_data'),
  },
  {
    name: 'pick_plan',
    text: t('wavy_onboarding.trial_tasks.tasks.pick_plan'),
  },
  {
    name: 'stop_wavy_subscription',
    text: t('wavy_onboarding.trial_tasks.tasks.stop_wavy'),
  },
  {
    name: 'migrate_website',
    text: t('wavy_onboarding.trial_tasks.tasks.migrate_website'),
  },
]);

const tasks = computed(() =>
  props.taskData.map((task) => ({
    name: task.task,
    text: taskTexts.value.find((text) => text.name === task.task)?.text,
    completedAt: task.completedAt,
    claimedAt: task.claimedAt,
  })),
);

const emit = defineEmits(['select']);

const onTaskClick = (taskName: string) => {
  emit('select', taskName);
};
</script>
